import React, { useEffect } from "react";
import { GuestCreditCard } from "./GuestCreditCard";
import "./CreditCardForm.scss";
import ErrorIcon from '@mui/icons-material/Error';
import { CreditCardFormHeader } from "./CreditCardFormHeader";
import { BookingFlowTriggerSource } from "../UILogicControl/UILogicControlEntities";
import { useAppState } from "../../Redux/ReduxHooks";
import { Checkbox } from "@mui/material";
import { Dispatch } from "../Dispatch";

/** Inline credit card form in the booking form that allows guest users to make bookings with credit/debit cards without signing in. This is implemented using Braintree SDK's Hosted Fields. Each input field is an iframe inside the provided div element for the input. All the input fields must be put together inside a form. */
export const CreditCardForm: React.FC = () => {

    const BookingTrigger = useAppState(i => i.uiLogicControl.BookingFlowTrigger);
    const [saveCard, setSaveCard] = React.useState(false);

    useEffect(() => {
        GuestCreditCard.StartHostedFields();
    }, []);

    /** Handle checkbox change for saving card details. */
    function OnSaveCardCheckboxChanged(e: React.ChangeEvent<HTMLInputElement>) {
        const checked = e.target.checked;
        setSaveCard(checked);
        Dispatch.PhoneToCnp.ToggleSaveCardForFutureTrips(checked);
    }

    let creditCardFrameClass = "credit-card-frame";
    let cardNumberLabel: string | JSX.Element = "Credit or Debit card number";
    let expiryLabel: string | JSX.Element = "Expiry date";
    let securityCodeLabel: string | JSX.Element = "CVV";
    let cvvErrorLabel = "Enter a valid CVV";

    if (BookingTrigger === BookingFlowTriggerSource.TrackingSms) {
        creditCardFrameClass = "credit-card-frame full-frame";
        cardNumberLabel = (<span className="cc-field-label">Card number&nbsp;<span className="required">*</span></span>);
        expiryLabel = (<span className="cc-field-label">Expiry&nbsp;<span className="required">*</span></span>);
        securityCodeLabel = (<span className="cc-field-label">Security code&nbsp;<span className="required">*</span></span>);
        cvvErrorLabel = "Enter a valid security code";
    }

    return (
        <div className={creditCardFrameClass}>
            <CreditCardFormHeader BookingTrigger={BookingTrigger} />
            <form id="cardForm" >
                <label className="hosted-fields-label" htmlFor="card-number">{cardNumberLabel}</label>
                <div id="card-number" className="hosted-field">
                    <span className="end-icon"><ErrorIcon fontSize="small" color="error" /></span>
                </div>
                <div className="card-input-error">Enter a valid card number</div>

                <div className="hosted-fields-group">
                    <div className="small-input-group">
                        <label className="hosted-fields-label" htmlFor="expiration-date">{expiryLabel}</label>
                        <div id="expiration-date" className="hosted-field">
                            <span className="end-icon"><ErrorIcon fontSize="small" color="error" /></span>
                        </div>
                        <div className="card-input-error">Enter a valid expiry date</div>
                    </div>

                    <div className="small-input-group">
                        <label className="hosted-fields-label" htmlFor="cvv">{securityCodeLabel}</label>
                        <div id="cvv" className="hosted-field">
                            <span className="end-icon"><ErrorIcon fontSize="small" color="error" /></span>
                        </div>
                        <div className="card-input-error">{cvvErrorLabel}</div>
                    </div>                    
                </div>
                
                {BookingTrigger === BookingFlowTriggerSource.TrackingSms && (
                    <div className="save-card-checkbox-container">
                        <Checkbox
                            id="save-card-checkbox"
                            checked={saveCard}
                            onChange={OnSaveCardCheckboxChanged}
                            className="save-card-checkbox"
                            sx={{
                                color: '#1A1A1A',
                                '&.Mui-checked': {
                                    color: '#802C00',
                                },
                            }}
                        />
                        <label htmlFor="save-card-checkbox" className="save-card-label">Save card details for future trips</label>
                    </div>
                )}
            </form>
        </div>
    );
}
